import {
  DynamicItemsConfigEntry,
  IDynamicItem,
  IDynamicItemMergeOptions,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import {
  isArrayOfRecommendationsPanelConfigItemWithConditions,
  RecommendationPanelRequirements,
  RecommendationsPanelConfigItem,
  RecommendationsPanelConfigItemWithConditions,
  RecommendationsPanels,
} from '@epicuro-next/models/recommendation-panels';
import { selectRecommendationsPanelsRule } from '@epicuro-next/platform/configuration';
import { selectHasActiveBasketId } from '@epicuro-next/state/basket-state';
import { selectUrl } from '@epicuro-next/state/router-store';
import {
  ensureArray,
  isNullish,
  orAndRegexMatcher,
} from '@epicuro-next/utilities';
import { createSelector } from '@ngrx/store';

export const selectRecommendationsPanelsItemsConfig = createSelector(
  selectRecommendationsPanelsRule,
  selectUrl,
  selectHasActiveBasketId,
  (recommendationsPanels, url, hasActiveBasketId) => {
    const recommendationsPanelsItemsConfig = getEmptyConfig();

    for (const [key, recommendationPanelConfig] of Object.entries(
      recommendationsPanels,
    )) {
      recommendationsPanelsItemsConfig[key as RecommendationsPanels] =
        getConfigItems(recommendationPanelConfig, url, {
          [RecommendationPanelRequirements.basketIsNotEmpty]: hasActiveBasketId,
        });
    }

    return recommendationsPanelsItemsConfig;
  },
);

function getEmptyConfig(): {
  [key in RecommendationsPanels]: DynamicItemsConfigEntry[];
} {
  return {
    [RecommendationsPanels.AddedToBasketNotification]: [],
    [RecommendationsPanels.AddedToBasketNotificationPane]: [],
    [RecommendationsPanels.BelowCBasket]: [],
    [RecommendationsPanels.BelowConfigStrip]: [],
    [RecommendationsPanels.Checkout]: [],
    [RecommendationsPanels.Checkout2]: [],
    [RecommendationsPanels.Spotlight]: [],
    [RecommendationsPanels.PBasket]: [],
    [RecommendationsPanels.SearchBar]: [],
    [RecommendationsPanels.SearchSuggestBar]: [],
    [RecommendationsPanels.SearchProducts]: [],
    [RecommendationsPanels.ProductBasketHeader]: [],
  };
}

function getConfigItems(
  recommendationPanelConfig: RecommendationsPanelConfigItem,
  url: string,
  dataForRequirements: Record<RecommendationPanelRequirements, boolean>,
) {
  const recommendationPanelConfigValues =
    ensureArrayOfRecommendationsPanelConfigItem(recommendationPanelConfig);

  if (
    isArrayOfRecommendationsPanelConfigItemWithConditions(
      recommendationPanelConfigValues,
    )
  ) {
    return getItemsFromRecommendationsPanelConfigItemWithConditions(
      recommendationPanelConfigValues,
      url,
      dataForRequirements,
    );
  }

  return recommendationPanelConfigValues;
}

function ensureArrayOfRecommendationsPanelConfigItem(
  recommendationPanelConfig: RecommendationsPanelConfigItem,
): DynamicItemsConfigEntry[] | RecommendationsPanelConfigItemWithConditions[] {
  return ensureArray<
    DynamicItemsConfigEntry | RecommendationsPanelConfigItemWithConditions
  >(recommendationPanelConfig) as
    | DynamicItemsConfigEntry[]
    | RecommendationsPanelConfigItemWithConditions[];
}

function getItemsFromRecommendationsPanelConfigItemWithConditions(
  recommendationPanelConfigValues: RecommendationsPanelConfigItemWithConditions[],
  url: string,
  dataForRequirements: Record<RecommendationPanelRequirements, boolean>,
): (Partial<IDynamicItem> | IDynamicItemMergeOptions)[] {
  let items: typeof recommendationPanelConfigValues[0]['items'] = [];

  for (const recommendationPanelConfigValue of recommendationPanelConfigValues) {
    if (
      isNullish(recommendationPanelConfigValue.pathPredicate) ||
      orAndRegexMatcher(url, recommendationPanelConfigValue.pathPredicate)
    ) {
      const requirements = recommendationPanelConfigValue.requirements;
      if (
        requirements === undefined ||
        requirementsAreMet(requirements, dataForRequirements)
      ) {
        items = recommendationPanelConfigValue.items;
        break;
      }
    }
  }

  return items;
}

export function requirementsAreMet(
  requirements: Exclude<
    RecommendationsPanelConfigItemWithConditions['requirements'],
    undefined
  >,
  data: Record<RecommendationPanelRequirements, boolean>,
  depth = 0,
): boolean {
  if (!Array.isArray(requirements)) {
    return requirementIsMet(requirements, data);
  } else {
    if (requirements.length === 0) {
      return true;
    }

    const nextDepth = depth + 1;
    const results: boolean[] = [];
    for (const requirement of requirements) {
      const result = requirementsAreMet(requirement, data, nextDepth);
      results.push(result);
    }

    if (depth === 0) {
      // First level of array is OR operation.
      // So ['requirementOne', 'requirementTwo'] = 'requirementOne' OR 'requirementTwo'
      return results.some((r) => r);
    } else {
      // Second level of array is AND operation.
      // So [['requirementOne', 'requirementTwo']] = 'requirementOne' AND 'requirementTwo'
      return results.every((r) => r);
    }
  }
}

export function requirementIsMet(
  requirement: RecommendationPanelRequirements,
  data: Record<RecommendationPanelRequirements, boolean>,
): boolean {
  const value = data[requirement];
  if (value === undefined) {
    console.warn(
      `RecommendationPanelRequirements '${requirement}' is not implemented, returning false by default therefore.`,
    );
    return false;
  }

  return value;
}
